import { createSelector } from 'reselect'
import { OrderedSet, Map } from 'immutable'

import { isArchived, isInvited } from './record'
import { getEntityMap, getEntity, getListState } from 'data'
import { getActiveUserId, getActiveNoteId } from 'data/ui/selectors'
import {
  getOrganizationPermissions,
  isPaid,
  isFree,
} from 'data/organizations/selectors'
import { normalize } from 'helpers/StringHelper'
import { checkIfPopularDomain } from 'helpers/PopularDomain'

import { OWNER, GUEST } from './roles'

export const PAGINATED_USERS_LIST_NAME = 'progressivePaginatedUsersLoad'

/**
 * ! In relation to getAllPaginatedUsersListState and getAllPaginatedUsers defined below:
 * We currently have a circular dependency issue in our import tree resolution.
 * This makes the getListState function not be available at the module level here and therefore we need
 * to have an intermediate for now at definition time. The function will be available afterwards once all
 * modules have been resolved.
 * Although far from ideal, this will stay here until we have resolved all the circular dependency issues in the app.
 */
export const getAllPaginatedUsersListState = state =>
  getListState(PAGINATED_USERS_LIST_NAME)(state)

export const getAllPaginatedUsers = state =>
  createSelector(
    state => getAllPaginatedUsersListState(state),
    state => getAllUsers(state, { filterOutInvited: false }),
    (listState, allUsers) => {
      return listState.nodes
        .map(userId => {
          return allUsers.get(userId)
        })
        .filter(Boolean)
    }
  )(state)

export const getAllUsers = (
  state,
  { filterOutArchived = false, filterOutInvited = false } = {}
) => {
  const map = getEntityMap(state, 'User')
  const mapFilteredArchived = filterOutArchived
    ? map.filter(user => !isArchived(user))
    : map
  const mapFilteredInvited = filterOutInvited
    ? mapFilteredArchived.filter(user => !isInvited(user))
    : mapFilteredArchived
  return mapFilteredInvited
}

export const getCurrentOrganizationUsers = createSelector(
  state => getEntityMap(state, 'User'),
  users => users.filter(user => !isArchived(user) && !isInvited(user))
)

export const getCurrentOrganizationUsersAsArray = createSelector(
  getCurrentOrganizationUsers,
  usersMap => usersMap.toArray()
)

export const getUser = (state, userId) => getEntity(state, 'User', userId)

export const isPopularDomain = state => {
  const user = getCurrentUser(state)
  const domain = user.email.split('@')[1]
  return checkIfPopularDomain(domain)
}

export const getUsers = (state, userIds, { filterOutArchived = false } = {}) =>
  getEntityMap(state, 'User')
    .filter(user => userIds.includes(user.id))
    .filter(user => !(filterOutArchived && isArchived(user)))

export const getCurrentUserId = getActiveUserId

export const getCurrentUser = state => {
  const userId = getActiveUserId(state)
  return getEntity(state, 'User', userId)
}

export const getUsersWithoutCurrent = (state, filterParams) =>
  getAllUsers(state, filterParams).remove(getCurrentUserId(state))

export const isCurrentUserTheOwner = state => {
  const currentUser = getCurrentUser(state)
  if (!currentUser) {
    return false
  }
  return currentUser.organizationRole === OWNER
}

export const isCurrentUserGuest = state => {
  const currentUser = getCurrentUser(state)
  if (!currentUser) {
    return false
  }
  return currentUser.organizationRole === GUEST
}

export const canCurrentUserManageOrganization = state =>
  getOrganizationPermissions(state).get('manage')

export const canCurrentUserManageUsers = state =>
  getOrganizationPermissions(state).get('manageUsers')

export const canCurrentUserManageChannels = state =>
  getOrganizationPermissions(state).get('manageChannels')

export const canCurrentUserManageIntegrations = state =>
  getOrganizationPermissions(state).get('manageIntegrations')

export const canCurrentUserManageOrganizationBilling = state =>
  getOrganizationPermissions(state).get('manageBilling')

export const canCurrentUserInviteMembers = state =>
  getOrganizationPermissions(state).get('invite')

export const canCurrentUserCreateChannel = state =>
  getOrganizationPermissions(state).get('createChannel')

export const getUpdatePhotoState = state => state.users.get('updatePhoto')

export const getUpdateEmailState = state => state.users.get('updateEmail')

export const getOrganizationOwner = state => {
  const users = getAllUsers(state).toList()
  return users.find(user => user.organizationRole === OWNER)
}

export const getUpdateOrganizationRoleState = state =>
  state.users.get('updateOrganizationRole')

export const getUserIdsPresenceByNoteId = createSelector(
  state => getEntityMap(state, 'UserPresence'),
  state => getActiveNoteId(state),
  state => getCurrentUser(state).get('id'),
  (userPresences, activeNoteId, currentUserId) => {
    return userPresences
      .reduce((memo, { noteId, id }) => {
        return Boolean(noteId) && id !== currentUserId
          ? memo.update(noteId, OrderedSet(), uids => uids.concat(id))
          : memo
      }, Map())
      .update(activeNoteId, OrderedSet(), uids => uids.concat(currentUserId))
  }
)

export const getUserPresenceByNoteId = createSelector(
  state => getEntityMap(state, 'UserPresence'),
  (state, noteId) => noteId,
  (userPresences, noteId) => {
    return userPresences.reduce(
      (memo, userPresence) =>
        userPresence.get('noteId') === noteId ? memo.add(userPresence) : memo,
      OrderedSet()
    )
  }
)

export const searchUsers = (state, searchTerm = '') => {
  const formatName = name => {
    if (!name) {
      return ''
    }
    return normalize(name)
  }
  const users = getAllUsers(state, { filterOutArchived: true })
  const query = formatName(searchTerm)

  return users
    .filter(user => {
      return (
        formatName(
          user.username + user.displayName + user.get('email')
        ).indexOf(query) > -1
      )
    })
    .toList()
}

export const hasAccessToPaidBillingViews = state => {
  return isPaid(state) && canCurrentUserManageOrganizationBilling(state)
}

export const hasAccessToFreeBillingViews = state => {
  return isFree(state)
}

export const getAbFlags = state => {
  const user = getCurrentUser(state)

  if (!user) {
    return ''
  }

  return user.abFlags
}

export const hasAbFlag = (state, flag) => {
  return getAbFlags(state).includes(flag)
}
